import { MailOpen } from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { useUiStore, type MailReadingLayout } from '../../stores/ui.js';

type MailPane = 'list' | 'detail';

interface MailPaneContextValue {
  split: boolean;
  activePane: MailPane;
}

const MailPaneContext = createContext<MailPaneContextValue>({
  split: false,
  activePane: 'list',
});

export function useMailPaneKeyboard(pane: MailPane): boolean {
  const context = useContext(MailPaneContext);
  return !context.split || context.activePane === pane;
}

export function useIsSplitMailView(): boolean {
  return useContext(MailPaneContext).split;
}

export function useEffectiveMailLayout(): MailReadingLayout {
  const preferred = useUiStore((state) => state.mailReadingLayout);
  const navCollapsed = useUiStore((state) => state.navCollapsed);
  const minimumWidth = navCollapsed ? 920 : 1_180;
  const [wideEnough, setWideEnough] = useState(() =>
    window.matchMedia(`(min-width: ${minimumWidth}px)`).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(`(min-width: ${minimumWidth}px)`);
    const update = () => setWideEnough(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, [minimumWidth]);

  return preferred === 'full' || !wideEnough ? 'full' : preferred;
}

export function MailWorkspace({
  layout,
  list,
  detail,
}: {
  layout: Exclude<MailReadingLayout, 'full'>;
  list: ReactNode;
  detail: ReactNode | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeCleanup = useRef<(() => void) | null>(null);
  const verticalPercent = useUiStore((state) => state.verticalSplitPercent);
  const horizontalPercent = useUiStore((state) => state.horizontalSplitPercent);
  const setVerticalPercent = useUiStore((state) => state.setVerticalSplitPercent);
  const setHorizontalPercent = useUiStore((state) => state.setHorizontalSplitPercent);
  const [activePane, setActivePane] = useState<MailPane>('list');
  const percent = layout === 'vertical' ? verticalPercent : horizontalPercent;

  useEffect(() => {
    if (detail === null) setActivePane('list');
  }, [detail]);

  useEffect(
    () => () => {
      resizeCleanup.current?.();
    },
    [],
  );

  const context = useMemo<MailPaneContextValue>(
    () => ({ split: true, activePane }),
    [activePane],
  );

  const updatePercent = (value: number) => {
    const bounded = Math.max(30, Math.min(70, Math.round(value)));
    if (layout === 'vertical') setVerticalPercent(bounded);
    else setHorizontalPercent(bounded);
  };

  const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    resizeCleanup.current?.();
    const previousCursor = document.body.style.cursor;
    const previousSelection = document.body.style.userSelect;
    document.body.style.cursor = layout === 'vertical' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';

    const move = (pointer: PointerEvent) => {
      const bounds = container.getBoundingClientRect();
      const next =
        layout === 'vertical'
          ? ((pointer.clientX - bounds.left) / bounds.width) * 100
          : ((pointer.clientY - bounds.top) / bounds.height) * 100;
      updatePercent(next);
    };
    const finish = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelection;
      resizeCleanup.current = null;
    };
    resizeCleanup.current = finish;
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
  };

  const resizeWithKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    const decrease = layout === 'vertical' ? 'ArrowLeft' : 'ArrowUp';
    const increase = layout === 'vertical' ? 'ArrowRight' : 'ArrowDown';
    if (event.key === decrease) {
      event.preventDefault();
      updatePercent(percent - (event.shiftKey ? 5 : 2));
    } else if (event.key === increase) {
      event.preventDefault();
      updatePercent(percent + (event.shiftKey ? 5 : 2));
    } else if (event.key === 'Home') {
      event.preventDefault();
      updatePercent(30);
    } else if (event.key === 'End') {
      event.preventDefault();
      updatePercent(70);
    }
  };

  return (
    <MailPaneContext.Provider value={context}>
      <div
        ref={containerRef}
        className={`flex h-full min-h-0 min-w-0 overflow-hidden ${
          layout === 'vertical' ? 'flex-row' : 'flex-col'
        }`}
      >
        <section
          data-mail-pane="list"
          aria-label="Message list"
          onPointerDown={() => setActivePane('list')}
          onFocusCapture={() => setActivePane('list')}
          className="min-h-0 min-w-0 overflow-hidden bg-app"
          style={
            layout === 'vertical'
              ? { width: `${percent}%`, minWidth: 280 }
              : { height: `${percent}%`, minHeight: 160 }
          }
        >
          {list}
        </section>

        <div
          role="separator"
          tabIndex={0}
          aria-label={layout === 'vertical' ? 'Resize message list width' : 'Resize message list height'}
          aria-orientation={layout === 'vertical' ? 'vertical' : 'horizontal'}
          aria-valuemin={30}
          aria-valuemax={70}
          aria-valuenow={percent}
          title="Drag to resize; double-click to reset"
          onPointerDown={startResize}
          onDoubleClick={() => updatePercent(layout === 'vertical' ? 42 : 45)}
          onKeyDown={resizeWithKeyboard}
          className={`group relative z-20 shrink-0 touch-none bg-line outline-none focus:bg-accent ${
            layout === 'vertical'
              ? 'w-1.5 cursor-col-resize'
              : 'h-1.5 cursor-row-resize'
          }`}
        >
          <span
            aria-hidden="true"
            className={`absolute bg-accent opacity-0 transition-opacity group-hover:opacity-100 group-focus:opacity-100 ${
              layout === 'vertical' ? 'inset-y-0 left-0.5 w-0.5' : 'inset-x-0 top-0.5 h-0.5'
            }`}
          />
        </div>

        <section
          data-mail-pane="detail"
          aria-label="Conversation"
          onPointerDown={() => setActivePane('detail')}
          onFocusCapture={() => setActivePane('detail')}
          className="min-h-0 min-w-0 flex-1 overflow-hidden bg-app"
        >
          {detail ?? <EmptyReadingPane layout={layout} />}
        </section>
      </div>
    </MailPaneContext.Provider>
  );
}

function EmptyReadingPane({ layout }: { layout: Exclude<MailReadingLayout, 'full'> }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-ink-muted">
      <MailOpen className="size-12 opacity-35" />
      <div>
        <p className="font-medium text-ink">Select a conversation</p>
        <p className="mt-1 text-sm">
          It will open in the {layout === 'vertical' ? 'right' : 'bottom'} reading pane.
        </p>
      </div>
    </div>
  );
}
